import { clsx } from 'clsx'
import { type FC } from 'react'
import { useIntl } from 'react-intl'

import { OfferV1OfferTypeEnum } from '@redteclab/api/clients/bully'
import { IconInfo } from '@redteclab/icons'

import { Notification } from 'base-ui'

import { Experiment } from '../../../../experiments/components/Experiment'
import { ExperimentVariant } from '../../../../experiments/components/ExperimentVariant'
import { EXPERIMENT_NAME_MARKETPLACE_HARMONISATION_PDP } from '../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../../experiments/model/EXPERIMENT_VARIATION'
import { useOpenableControls } from '../../../../generic-hooks'
import { productAccessorGetPharmaBlockedAlternativeText } from '../../../model/productAccessor/productAccessorGetPharmaBlockedAlternativeText'
import { productAccessorGetSellerPromotionFromOffer } from '../../../model/productAccessor/productAccessorGetSellerPromotionFromOffer'
import { productAccessorHasZavaOdsPartner } from '../../../model/productAccessor/productAccessorHasZavaOdsPartner'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorIsProductNotInCatalog } from '../../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../../model/productAccessor/productAccessorIsProductOutOfStock'
import { PRODUCT_TEMPLATES } from '../../../model/productTemplates'
import { ProductPageSellerPromotionModal } from '../../../ProductPageSellerPromotionModal/ProductPageSellerPromotionModal'
import { ProductDescriptionParsedField } from '../../ProductDescription/ProductDescriptionParsedField'
import { ProductHeadGenericLabel } from '../../ProductHead/ProductHeadLabels/ProductHeadGenericLabel/ProductHeadGenericLabel'
import { useProductPageContext } from '../../productPageContext'
import { ProductPageOdsDialogCtaButton } from '../../ProductPageOdsDialog/ProductPageOdsDialogCtaButton'
import { ProductPageVolumePricingBadge } from '../../ProductPageVolumePricingBadge/ProductPageVolumePricingBadge'
import { useShouldRenderVolumePricingLabel } from '../../ProductPageVolumePricingBadge/useShouldRenderVolumePricingBadge'
import { ProductPrescriptionTypeSelect } from '../../ProductPrescriptionType'
import { ProductSellerPromotionFlag } from '../../ProductSellerPromotionFlag/ProductSellerPromotionFlag'
import { useHideAddToCart } from '../useHideAddToCart'

import { ProductAddToCartButtonRow } from './ProductAddToCartButtonRow'
import { ProductAddToCartGroupPriceInfo } from './ProductAddToCartGroupPriceInfo/ProductAddToCartGroupPriceInfo'

const useShallOdsButtonRender = (): boolean => {
  const { currentVariant, productTemplate } = useProductPageContext()

  return (
    productTemplate === PRODUCT_TEMPLATES.RX &&
    productAccessorHasZavaOdsPartner(currentVariant)
  )
}

const useRenderPrescriptionSelect = (): boolean => {
  const { currentOffer, currentVariant, productTemplate } =
    useProductPageContext()

  if (
    productAccessorIsProductOutOfStock(currentVariant.stock_status) ||
    productAccessorIsProductOutOfSale(currentVariant.stock_status) ||
    productAccessorIsProductNotInCatalog(currentVariant.stock_status)
  ) {
    return false
  }

  const isRx = productTemplate === PRODUCT_TEMPLATES.RX

  return currentOffer?.offerType
    ? isRx && currentOffer.offerType === OfferV1OfferTypeEnum.Sae
    : isRx
}

const useShouldRenderPharmaBlockedAlternativeText = (): boolean => {
  const { product } = useProductPageContext()

  if (
    !productAccessorIsProductOutOfStock(product.stock_status) ||
    !productAccessorIsProductOutOfSale(product.stock_status) ||
    !productAccessorIsProductNotInCatalog(product.stock_status)
  ) {
    return true
  }

  return false
}

export const ProductAddToCartGroup: FC = () => {
  const hideAddToCart = useHideAddToCart()
  const i18n = useIntl()
  const { product } = useProductPageContext()
  const renderPrescriptionSelect = useRenderPrescriptionSelect()
  const isPrescription = productAccessorIsPrescriptionProduct(product)
  const shouldShowOdsButton = useShallOdsButtonRender()
  const pharmaBlockedAlternativeText =
    productAccessorGetPharmaBlockedAlternativeText(product)
  const renderPharmaBlockedAlternativeText =
    useShouldRenderPharmaBlockedAlternativeText()
  const renderVolumeBasedPricingLabel = useShouldRenderVolumePricingLabel()
  const { currentOffer } = useProductPageContext()
  const { handleClose, handleOpen, isOpen } = useOpenableControls()

  const sellerPromotion =
    currentOffer && productAccessorGetSellerPromotionFromOffer(currentOffer)

  if (hideAddToCart) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      <Experiment names={[EXPERIMENT_NAME_MARKETPLACE_HARMONISATION_PDP]}>
        <ExperimentVariant name={EXPERIMENT_VARIATION.DEFAULT}>
          {renderVolumeBasedPricingLabel ? (
            <ProductPageVolumePricingBadge />
          ) : null}
        </ExperimentVariant>
        <ExperimentVariant name={EXPERIMENT_VARIATION.V1}>
          <div className="flex flex-wrap">
            {renderVolumeBasedPricingLabel ? (
              <ProductPageVolumePricingBadge />
            ) : null}
            {sellerPromotion ? (
              <>
                <ProductHeadGenericLabel
                  className="border-none bg-light-tertiary"
                  data-qa-id="product-label-seller-promotion"
                  onClick={() => {
                    handleOpen()
                  }}
                >
                  <ProductSellerPromotionFlag
                    sellerPromotion={sellerPromotion}
                  />
                  <IconInfo className="ml-1 size-4 shrink-0" />
                </ProductHeadGenericLabel>
                <ProductPageSellerPromotionModal
                  dialogTitle={
                    <ProductSellerPromotionFlag
                      sellerPromotion={sellerPromotion}
                    />
                  }
                  isOpen={isOpen}
                  offer={currentOffer}
                  onClose={handleClose}
                  product={product}
                />
              </>
            ) : null}
          </div>
        </ExperimentVariant>
      </Experiment>
      <div
        className={clsx(
          'mb-2.5 gap-2.5',
          renderPrescriptionSelect
            ? 'grid grid-cols-3 tablet:grid-cols-2'
            : 'flex',
        )}
      >
        {renderPrescriptionSelect ? (
          <ProductPrescriptionTypeSelect className="col-span-2 tablet:col-auto" />
        ) : null}

        <ProductAddToCartButtonRow
          renderPrescriptionSelect={renderPrescriptionSelect}
        />
      </div>

      {isPrescription &&
      renderPharmaBlockedAlternativeText &&
      pharmaBlockedAlternativeText ? (
        <Notification
          className="my-4 bg-light-primary-low"
          severity="info"
          title={i18n.formatMessage({
            id: 'product.details.pharmaBlockedAlternativeText',
          })}
        >
          <ProductDescriptionParsedField
            field={pharmaBlockedAlternativeText.toString()}
          />
        </Notification>
      ) : null}

      {shouldShowOdsButton ? <ProductPageOdsDialogCtaButton /> : null}
      <ProductAddToCartGroupPriceInfo />
    </div>
  )
}
